import { Schema } from "effect";
import { CandidateId, CommitId, TreeId } from "../../development-identifiers.ts";
import { StationPath } from "../../station-path.ts";
export { ChangeReviewError, changeReviewError } from "../../errors/change-review.ts";

export const REVIEW_MANIFEST_VERSION = 2 as const;
export const REVIEW_POLICY_VERSION = 1 as const;

export const SPECIALIST_ROLES = [
  "baseline-correctness",
  "security",
  "performance",
  "state-reliability",
  "test-verification",
  "documentation",
  "accessibility"
] as const;
export const SpecialistRole = /* @__PURE__ */ (() => Schema.Literals(SPECIALIST_ROLES))();
export type SpecialistRole = typeof SpecialistRole.Type;

export const FINDING_SEVERITIES = [
  "info",
  "low",
  "medium",
  "high",
  "critical"
] as const;
export const FindingSeverity = /* @__PURE__ */ (() => Schema.Literals(FINDING_SEVERITIES))();
export type FindingSeverity = typeof FindingSeverity.Type;

export const FINDING_CATEGORIES = [
  "correctness",
  "security",
  "performance",
  "state-reliability",
  "testing",
  "documentation",
  "accessibility"
] as const;
export const FindingCategory = /* @__PURE__ */ (() => Schema.Literals(FINDING_CATEGORIES))();
export type FindingCategory = typeof FindingCategory.Type;

export const REVIEW_RUN_STATES = [
  "queued",
  "running",
  "recovering",
  "completed",
  "failed",
  "cancelled",
  "skipped",
  "stale"
] as const;
export const ReviewRunState = /* @__PURE__ */ (() => Schema.Literals(REVIEW_RUN_STATES))();
export type ReviewRunState = typeof ReviewRunState.Type;

export const RISK_TIERS = ["low", "medium", "high", "critical"] as const;
export const RiskTier = /* @__PURE__ */ (() => Schema.Literals(RISK_TIERS))();
export type RiskTier = typeof RiskTier.Type;

export const ReviewToken = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9._:-]{0,127}$/)))();
export const ReviewDigest = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^(?:sha256:)?[a-f0-9]{64}$/)))();
export const ReviewGlob = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value =>
  value.length >= 1 && value.length <= 256 && !value.startsWith("/") && !value.endsWith("/") &&
  !value.includes("\\") && !value.includes("//") && !/[\x00-\x1f\x7f]/.test(value) &&
  /^[A-Za-z0-9._/@*?-]+$/.test(value) && value.split("/").every(part =>
    part !== "." && part !== ".." && (!part.includes("**") || part === "**")
  )
)))();

export const ReviewCoverageRecordInput = /* @__PURE__ */ (() => Schema.Struct({
  role: SpecialistRole.pipe(Schema.mutableKey),
  required: Schema.Boolean.pipe(Schema.mutableKey),
  files: Schema.Array(Schema.String).pipe(Schema.optional, Schema.mutableKey),
  domains: Schema.Array(ReviewToken).pipe(Schema.optional, Schema.mutableKey),
  state: ReviewRunState.pipe(Schema.optional, Schema.mutableKey),
}))();
export type ReviewCoverageRecordInput = typeof ReviewCoverageRecordInput.Encoded;

export const ReviewCoverageRecord = /* @__PURE__ */ (() => Schema.Struct({
  role: SpecialistRole,
  required: Schema.Boolean,
  files: Schema.Array(StationPath),
  domains: Schema.Array(ReviewToken),
  state: ReviewRunState,
}))();
export type ReviewCoverageRecord = typeof ReviewCoverageRecord.Type;

export const ReviewManifestInput = /* @__PURE__ */ (() => Schema.Struct({
  candidateId: CandidateId.pipe(Schema.mutableKey),
  candidateCommitId: CommitId.pipe(Schema.mutableKey),
  effectiveTreeId: TreeId.pipe(Schema.mutableKey),
  policyDigest: ReviewDigest.pipe(Schema.mutableKey),
  riskInputDigest: ReviewDigest.pipe(Schema.mutableKey),
  riskTier: RiskTier.pipe(Schema.mutableKey),
  riskSignals: Schema.Array(ReviewToken).pipe(Schema.mutableKey),
  coverage: Schema.Array(ReviewCoverageRecordInput).pipe(Schema.mutableKey),
}))();
export type ReviewManifestInput = typeof ReviewManifestInput.Encoded;

export const ReviewManifest = /* @__PURE__ */ (() => Schema.Struct({
  version: Schema.Literal(REVIEW_MANIFEST_VERSION),
  candidateId: CandidateId,
  candidateCommitId: CommitId,
  effectiveTreeId: TreeId,
  policyDigest: ReviewDigest,
  riskInputDigest: ReviewDigest,
  riskTier: RiskTier,
  riskSignals: Schema.Array(ReviewToken),
  coverage: Schema.Array(ReviewCoverageRecord),
  manifestDigest: ReviewDigest,
}))();
export type ReviewManifest = typeof ReviewManifest.Type;

export const ReviewRiskRule = /* @__PURE__ */ (() => Schema.Struct({
  id: ReviewToken,
  tier: RiskTier,
  signals: Schema.Array(ReviewToken),
  requiredRoles: Schema.Array(SpecialistRole),
  advisoryRoles: Schema.Array(SpecialistRole),
}))();
export type ReviewRiskRule = typeof ReviewRiskRule.Type;

export const ReviewRiskPolicy = /* @__PURE__ */ (() => Schema.Struct({
  version: Schema.Literal(REVIEW_POLICY_VERSION),
  rules: Schema.Array(ReviewRiskRule),
  generatedFilePatterns: Schema.Array(ReviewGlob),
  vendorFilePatterns: Schema.Array(ReviewGlob),
  neverExcludePatterns: Schema.Array(ReviewGlob),
}))();
export type ReviewRiskPolicy = typeof ReviewRiskPolicy.Type;

export const ReviewPolicyFileInput = /* @__PURE__ */ (() => Schema.Struct({
  path: Schema.String,
  generated: Schema.Boolean.pipe(Schema.optional),
  semanticKind: Schema.suspend(() => ReviewPolicyFileSemanticKind).pipe(Schema.optional),
}))();
export type ReviewPolicyFileInput = typeof ReviewPolicyFileInput.Type;

export const ReviewPolicyFileSemanticKind = /* @__PURE__ */ (() => Schema.Literals(["source", "migration", "lifecycle-declaration"]))();
export type ReviewPolicyFileSemanticKind = typeof ReviewPolicyFileSemanticKind.Type;

export const ReviewPolicyFileDisposition = /* @__PURE__ */ (() => Schema.Literals(["included", "protected-included", "generated-excluded", "vendor-excluded"]))();
export type ReviewPolicyFileDisposition = typeof ReviewPolicyFileDisposition.Type;

export const ReviewPolicyFileDecision = /* @__PURE__ */ (() => Schema.Struct({
  path: StationPath,
  included: Schema.Boolean,
  disposition: ReviewPolicyFileDisposition,
  matchedPattern: Schema.String.pipe(Schema.optional),
}))();
export type ReviewPolicyFileDecision = typeof ReviewPolicyFileDecision.Type;

export type ReviewManifestIdentity = Omit<ReviewManifest, "manifestDigest">;
